import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { errorMessage } from "@web/lib/api";
import { vendorCategoriesQuery, vendorDirectoryApi } from "@web/lib/queries-vendors";
import { BottomSheet, Button, Picker, Stack, Text, TextField, useToast } from "@web/ui";

/** Adding a vendor to the directory. Deliberately short: a name is the only
 * thing required, because the moment somebody wants to record a vendor is
 * usually the moment they have a business card and thirty seconds. Everything
 * else is editable afterwards from the detail sheet. */
export function NewVendorSheet({ open, onClose, onCreated, initialName = "" }: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  initialName?: string;
}) {
  const toast = useToast();
  const categories = useQuery(vendorCategoriesQuery);
  const [name, setName] = useState(initialName);
  const [categoryId, setCategoryId] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setName(""); setCategoryId(""); setContactName(""); setPhone(""); setEmail(""); };

  async function save() {
    setBusy(true);
    try {
      const vendor = await vendorDirectoryApi.create({
        name: name.trim(),
        categoryId: categoryId || null,
        contactName: contactName.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
      toast.success(`${vendor.name} added`);
      reset();
      onCreated(vendor.id);
    } catch (error) { toast.error(errorMessage(error)); }
    finally { setBusy(false); }
  }

  return (
    <BottomSheet open={open} onClose={() => { reset(); onClose(); }} title="Add a vendor"
      footer={<Button loading={busy} disabled={!name.trim()} onClick={() => void save()}>Add to the directory</Button>}>
      <Stack gap={3}>
        <TextField label="Business name" value={name} onChange={event => setName(event.target.value)} autoFocus maxLength={160} />
        <Picker label="Category" value={categoryId} onChange={setCategoryId}
          options={[{ value: "", label: "No category yet" }, ...(categories.data ?? []).map(category => ({ value: category.id, label: category.name }))]} />
        <TextField label="Who you deal with" value={contactName} onChange={event => setContactName(event.target.value)} maxLength={160} />
        <TextField label="Phone" type="tel" inputMode="tel" value={phone} onChange={event => setPhone(event.target.value)} maxLength={40} />
        <TextField label="Email" type="email" inputMode="email" value={email} onChange={event => setEmail(event.target.value)} maxLength={200} />
        <Text variant="caption" tone="tertiary">
          A name is enough to start. Add the certificate, the website and which venues prefer them from the vendor's own page.
        </Text>
      </Stack>
    </BottomSheet>
  );
}
